import type { UIMessage as Message } from 'ai';

/**
 * Sanitizes an array of chat messages by cleaning up tool invocations
 *
 * This function performs two main operations:
 * 1. For assistant messages with tool invocations, it filters the tool invocations to only keep:
 *    - Invocations that are in a terminal output state ('output-available' or 'output-error')
 *    - Invocations whose toolCallId matches a completed result
 * 2. Filters out messages that have no content and no tool invocations
 *
 * @param messages - Array of chat messages to sanitize
 * @returns Array of sanitized messages with cleaned up tool invocations
 */
export function sanitizeUIMessages<TMessage extends Message>(
  messages: Array<TMessage>,
): Array<TMessage> {
  const messagesBySanitizedToolInvocations = messages.map((message) => {
    if (message.role !== 'assistant') {
      return message;
    }

    if (!message.parts || !Array.isArray(message.parts)) {
      return message;
    }

    // Collect tool result IDs from completed tool invocations
    const toolResultIds: Array<string> = [];
    for (const part of message.parts) {
      if (
        part.type.startsWith('tool-') &&
        ((part as any).state === 'output-available' || (part as any).state === 'output-error')
      ) {
        toolResultIds.push((part as any).toolCallId);
      }
    }

    // Filter parts to only keep text and completed tool invocations
    const sanitizedParts = message.parts.filter((part) => {
      if (part.type === 'text') {
        // Keep text parts that have content
        return part.text && part.text.length > 0;
      }
      if (part.type.startsWith('tool-')) {
        const toolPart = part as any;
        // Keep tool parts that are completed or match a completed result
        return (
          toolPart.state === 'output-available' ||
          toolPart.state === 'output-error' ||
          toolResultIds.includes(toolPart.toolCallId)
        );
      }
      return true;
    });

    return {
      ...message,
      parts: sanitizedParts,
    };
  });

  // Filter out messages that have no parts or empty parts array
  return messagesBySanitizedToolInvocations.filter((message) => {
    if (!message.parts || !Array.isArray(message.parts)) {
      return false;
    }
    return message.parts.length > 0;
  });
}
